import React, { useEffect, useState } from 'react';
import { X, Loader2, RefreshCw, Save, History, FileBarChart2, Crosshair } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { competitorsAPI } from '../services/api';
import CompetitorUrlsEditor from './CompetitorUrlsEditor';
import CompetitorPositionBadge, { fmtEur, fmtDelta, fmtDate, hostOf } from './CompetitorPositionBadge';
import CompetitorPriceChart from './CompetitorPriceChart';
import AutoAlignToggle from './AutoAlignToggle';
import CompetitorManualPrices from './CompetitorManualPrices';

const ReportCard = ({ snap, similarPct }) => {
  const rep = snap.report;
  const position = rep.priced_count ? rep.position : 'not_fetched';
  return (
    <div className="border rounded-lg p-4 bg-gray-50" data-testid="competitor-report">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2"><FileBarChart2 size={16} className="text-orange-500" /> Rapport du {fmtDate(snap.fetched_at)}</h4>
        <CompetitorPositionBadge position={position} testId="competitor-report-position" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
        <div><p className="text-xs text-gray-500">Notre prix</p><p className="font-semibold text-gray-900" data-testid="competitor-report-our-price">{fmtEur(snap.our_price)}</p>
          {rep.evolution?.our_delta != null && <p className={`text-[11px] ${rep.evolution.our_delta > 0 ? 'text-red-600' : rep.evolution.our_delta < 0 ? 'text-green-700' : 'text-gray-500'}`}>{fmtDelta(rep.evolution.our_delta)} vs {rep.evolution.previous_month}</p>}
        </div>
        <div><p className="text-xs text-gray-500">Concurrent le moins cher</p><p className="font-semibold text-gray-900" data-testid="competitor-report-cheapest">{rep.cheapest_slot ? `C${rep.cheapest_slot} · ${fmtEur(rep.cheapest_price)}` : '—'}</p></div>
        <div><p className="text-xs text-gray-500">Écart avec le moins cher</p>
          {rep.cheapest_slot ? (() => { const d = rep.diffs.find(x => x.slot === rep.cheapest_slot); return <p className={`font-semibold ${d.diff_eur > 0 ? 'text-red-600' : d.diff_eur < 0 ? 'text-green-700' : 'text-gray-900'}`}>{fmtDelta(d.diff_eur, d.diff_pct)}</p>; })() : <p className="text-gray-400">—</p>}
        </div>
        <div><p className="text-xs text-gray-500">Seuil « similaire »</p><p className="font-semibold text-gray-900">± {similarPct} %</p></div>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-gray-500 text-left"><th className="py-1">Concurrent</th><th className="py-1">Prix</th><th className="py-1">Ancien prix</th><th className="py-1">Dispo</th><th className="py-1">Livraison</th><th className="py-1">Écart</th><th className="py-1">Évolution</th></tr></thead>
        <tbody>
          {snap.competitors.map(c => {
            const d = rep.diffs.find(x => x.slot === c.slot) || {};
            const e = rep.evolution?.competitors?.find(x => x.slot === c.slot) || {};
            return (
              <tr key={c.slot} className="border-t" data-testid={`competitor-report-row-${c.slot}`}>
                <td className="py-1.5"><a href={c.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">C{c.slot} · {hostOf(c.url)}</a>{c.method && <span className="ml-1 text-[10px] text-gray-400">({c.method === 'manual' ? `saisi à la main${c.carried ? ', conservé' : ''}` : c.method})</span>}</td>
                <td className="py-1.5 font-medium">{c.status === 'ok' && c.price != null ? fmtEur(c.price) : <span className="text-gray-500 font-normal text-xs" title={c.error || ''}>Prix non récupéré{c.error ? ` (${c.error})` : ''}</span>}</td>
                <td className="py-1.5 text-gray-500">{c.old_price ? <span className="line-through">{fmtEur(c.old_price)}</span> : '—'}</td>
                <td className="py-1.5 text-gray-600">{c.availability || '—'}</td>
                <td className="py-1.5 text-gray-600">{c.shipping == null ? '—' : c.shipping === 0 ? 'Offerte' : fmtEur(c.shipping)}</td>
                <td className={`py-1.5 font-medium ${d.diff_eur > 0 ? 'text-red-600' : d.diff_eur < 0 ? 'text-green-700' : 'text-gray-700'}`}>{d.diff_eur != null ? fmtDelta(d.diff_eur, d.diff_pct) : '—'}</td>
                <td className={`py-1.5 ${e.delta_eur > 0 ? 'text-red-600' : e.delta_eur < 0 ? 'text-green-700' : 'text-gray-500'}`}>{e.delta_eur != null ? fmtDelta(e.delta_eur, e.delta_pct) : (rep.evolution?.previous_month ? '—' : 'Premier relevé')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CompetitorProductDrawer = ({ productId, onClose, onChanged, onRun, running, onEditProduct, onAlign, alignKey, minMarginPct }) => {
  const [data, setData] = useState(null);
  const [urls, setUrls] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => competitorsAPI.getProduct(productId).then(d => { setData(d); setUrls(d.product.competitor_urls); }).catch(() => toast.error('Chargement impossible'));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [productId, running, alignKey]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await competitorsAPI.setUrls(productId, urls);
      setUrls(r.competitor_urls);
      toast.success('URLs concurrentes enregistrées');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const history = data?.history || [];
  const latest = history[0];
  const dirty = data && JSON.stringify(urls.map(u => (u || '').trim()).filter(Boolean)) !== JSON.stringify(data.product.competitor_urls);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="competitor-drawer">
      <div className="w-full max-w-3xl h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-start justify-between gap-3 z-10">
          {data ? (
            <div className="flex items-center gap-3 min-w-0">
              <img src={data.product.image} alt="" className="w-12 h-12 object-contain bg-gray-50 rounded" />
              <div className="min-w-0">
                <h2 className="font-bold text-gray-900 truncate" data-testid="competitor-drawer-title">{data.product.name}</h2>
                <p className="text-xs text-gray-500">{data.product.brand} · Réf. {data.product.ref} · Notre prix <strong>{fmtEur(data.product.price)}</strong></p>
              </div>
            </div>
          ) : <Loader2 className="animate-spin text-orange-500" />}
          <div className="flex items-center gap-2 shrink-0">
            {latest?.report?.position === 'more_expensive' && latest.report.priced_count > 0 && (
              <Button size="sm" data-testid="competitor-drawer-align" onClick={() => onAlign?.(data.product.name)} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"><Crosshair size={14} className="mr-1" /> Aligner mon prix</Button>
            )}
            <Button size="sm" variant="outline" data-testid="competitor-drawer-run" onClick={onRun} disabled={running || !urls.some(Boolean)}>{running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />} Relever maintenant</Button>
            <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded" data-testid="competitor-drawer-close"><X size={18} /></button>
          </div>
        </div>

        {data && (
          <div className="p-6 space-y-6">
            <section className="border rounded-lg p-4">
              <CompetitorUrlsEditor urls={urls} onChange={setUrls} />
              <div className="flex items-center justify-end gap-2 mt-3">
                {onEditProduct && <Button size="sm" variant="ghost" onClick={() => { onClose(); onEditProduct(productId); }} data-testid="competitor-drawer-edit-product">Ouvrir la fiche produit</Button>}
                <Button size="sm" data-testid="competitor-drawer-save-urls" onClick={save} disabled={saving || !dirty} className="bg-green-600 hover:bg-green-700">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Enregistrer les URLs</Button>
              </div>
            </section>

            <AutoAlignToggle key={`${productId}-${data.product.auto_align}-${data.product.auto_align_offset}`} productId={productId} autoAlign={data.product.auto_align} offset={data.product.auto_align_offset} minMarginPct={minMarginPct} onSaved={(r) => { setData(d => ({ ...d, product: { ...d.product, ...r } })); onChanged?.(); }} />

            <CompetitorManualPrices productId={productId} urls={data.product.competitor_urls} latest={latest} onSaved={() => { load(); onChanged?.(); }} />

            {latest ? <ReportCard snap={latest} similarPct={data.similar_pct} /> : (
              <p className="text-sm text-gray-500 border rounded-lg p-4" data-testid="competitor-no-report">Aucun relevé pour ce produit. Enregistrez des URLs puis cliquez sur « Relever maintenant ».</p>
            )}

            {history.length > 0 && <CompetitorPriceChart history={history} urls={data.product.competitor_urls} />}

            <section>
              <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-2"><History size={16} className="text-orange-500" /> Historique des relevés</h4>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm" data-testid="competitor-history-table">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase"><tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Notre prix</th><th className="px-3 py-2 text-left">Concurrent 1</th><th className="px-3 py-2 text-left">Concurrent 2</th><th className="px-3 py-2 text-left">Concurrent 3</th><th className="px-3 py-2 text-left">Position</th></tr></thead>
                  <tbody className="divide-y">
                    {history.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Aucun relevé</td></tr>}
                    {history.map(s => (
                      <tr key={s.id} data-testid={`competitor-history-row-${s.id}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{fmtDate(s.fetched_at)}<span className="block text-[10px] text-gray-400">{s.trigger === 'auto' ? 'automatique' : 'manuel'}</span></td>
                        <td className="px-3 py-2 font-medium">{fmtEur(s.our_price)}</td>
                        {[1, 2, 3].map(slot => { const c = s.competitors.find(x => x.slot === slot); return <td key={slot} className="px-3 py-2">{!c ? <span className="text-gray-300">—</span> : c.status === 'ok' && c.price != null ? <>{fmtEur(c.price)}{c.old_price ? <span className="ml-1 text-xs text-gray-400 line-through">{fmtEur(c.old_price)}</span> : null}</> : <span className="text-xs text-gray-500">Non récupéré</span>}</td>; })}
                        <td className="px-3 py-2"><CompetitorPositionBadge position={s.report.priced_count ? s.report.position : 'not_fetched'} short /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitorProductDrawer;
